// SVG node-link lineage graph.  Three columns: sources → transforms → targets.
// nodes: [{ id, column: 0|1|2, label, sublabel, tone? }]
// edges: [{ from, to, conflict?: boolean, dashed?: boolean }]

import { useOptionalRichTooltipController } from '../richTooltips/RichTooltipProvider.jsx';

export function LineagePanel({
  nodes = [],
  edges = [],
  selectedEdgeId,
  onSelectEdge,
  onDrill,
  height = 360,
  ariaLabel = 'Lineage map',
}) {
  const tooltip = useOptionalRichTooltipController();
  const cols = [[], [], []];
  for (const n of nodes) {
    const c = n.column ?? 0;
    if (c < 0 || c > 2) continue;
    cols[c].push(n);
  }
  const colXs = [120, 460, 800];
  const baseY = 80;
  const stepY = 80;
  const positions = new Map();
  for (let c = 0; c < 3; c++) {
    const list = cols[c];
    list.forEach((n, i) => {
      positions.set(n.id, { x: colXs[c], y: baseY + i * stepY });
    });
  }
  const width = 960;
  const calcHeight = Math.max(
    height,
    baseY + Math.max(0, Math.max(...cols.map((c) => c.length - 1))) * stepY + 100,
  );

  return (
    <div className="lineage-panel" role="img" aria-label={ariaLabel}>
      <svg viewBox={`0 0 ${width} ${calcHeight}`} width="100%" height={calcHeight}>
        <defs>
          <marker
            id="lin-arrow"
            viewBox="0 0 8 8"
            refX="7"
            refY="4"
            markerWidth="6"
            markerHeight="6"
            orient="auto"
          >
            <path d="M0 0 L8 4 L0 8 z" fill="#1961a1" />
          </marker>
          <marker
            id="lin-arrow-warn"
            viewBox="0 0 8 8"
            refX="7"
            refY="4"
            markerWidth="6"
            markerHeight="6"
            orient="auto"
          >
            <path d="M0 0 L8 4 L0 8 z" fill="#c0524d" />
          </marker>
        </defs>
        {/* column titles */}
        {['Source systems', 'Transforms', 'Canonical entities'].map((t, i) => (
          <text key={t} x={colXs[i]} y={40} textAnchor="middle" className="lineage-col">
            {t}
          </text>
        ))}
        {/* edges */}
        {edges.map((e, i) => {
          const a = positions.get(e.from);
          const b = positions.get(e.to);
          if (!a || !b) return null;
          const midX = (a.x + b.x) / 2;
          const d = `M ${a.x + 80} ${a.y} C ${midX} ${a.y}, ${midX} ${b.y}, ${b.x - 80} ${b.y}`;
          const active = Boolean(
            e.richTooltip?.surfaceId && tooltip?.isActive(e.richTooltip.surfaceId),
          );
          const pinned = Boolean(
            e.richTooltip?.surfaceId && tooltip?.isPinned(e.richTooltip.surfaceId),
          );
          const cls = `lineage-edge ${e.conflict ? 'conflict' : ''} ${selectedEdgeId === e.id ? 'selected' : ''}${active ? ' is-active' : ''}${pinned ? ' is-pinned' : ''}`;
          const triggerProps =
            e.richTooltip && tooltip
              ? tooltip.getTriggerProps({
                  surfaceId: e.richTooltip.surfaceId,
                  payload: e.richTooltip,
                  testIdPrefix: 'rich-tooltip',
                  onDrill,
                  ariaLabel: `${e.label || e.id} lineage edge details`,
                  onClick: () => onSelectEdge && onSelectEdge(e),
                })
              : {};
          return (
            <path
              key={e.id || i}
              d={d}
              className={cls}
              strokeDasharray={e.dashed ? '6 6' : 'none'}
              markerEnd={e.conflict ? 'url(#lin-arrow-warn)' : 'url(#lin-arrow)'}
              onClick={!e.richTooltip ? () => onSelectEdge && onSelectEdge(e) : undefined}
              style={{ cursor: onSelectEdge || e.richTooltip ? 'pointer' : 'default' }}
              data-testid={`lineage-edge-${e.id}`}
              role={e.richTooltip ? 'button' : undefined}
              tabIndex={e.richTooltip ? 0 : undefined}
              {...triggerProps}
            />
          );
        })}
        {/* nodes */}
        {nodes.map((n) => {
          const p = positions.get(n.id);
          if (!p) return null;
          const active = Boolean(
            n.richTooltip?.surfaceId && tooltip?.isActive(n.richTooltip.surfaceId),
          );
          const pinned = Boolean(
            n.richTooltip?.surfaceId && tooltip?.isPinned(n.richTooltip.surfaceId),
          );
          const triggerProps =
            n.richTooltip && tooltip
              ? tooltip.getTriggerProps({
                  surfaceId: n.richTooltip.surfaceId,
                  payload: n.richTooltip,
                  testIdPrefix: 'rich-tooltip',
                  onDrill,
                  ariaLabel: `${n.label} lineage node details`,
                })
              : {};
          return (
            <g
              key={n.id}
              className={`lineage-node tone-${n.tone || 'blue'}${active ? ' is-active' : ''}${pinned ? ' is-pinned' : ''}`}
              transform={`translate(${p.x - 80} ${p.y - 24})`}
              data-testid={`lineage-node-${n.id}`}
              role={n.richTooltip ? 'button' : undefined}
              tabIndex={n.richTooltip ? 0 : undefined}
              style={{ cursor: n.richTooltip ? 'pointer' : 'default' }}
              {...triggerProps}
            >
              <rect width="160" height="48" rx="10" />
              <text x="80" y="20" textAnchor="middle" className="lineage-node-label">
                {n.label}
              </text>
              {n.sublabel ? (
                <text x="80" y="36" textAnchor="middle" className="lineage-node-sub">
                  {n.sublabel}
                </text>
              ) : null}
            </g>
          );
        })}
      </svg>
    </div>
  );
}
